import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

/* ── Vérifie si le token stocké est expiré ── */
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    // exp est en secondes, Date.now() en ms
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

/*const updateUser = (newUserData) => {
  setUser(prev => ({ ...prev, ...newUserData }));
  // Si vous persistez l'utilisateur dans localStorage, mettez-le aussi à jour :
  localStorage.setItem('user', JSON.stringify({ ...user, ...newUserData }));
};*/

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken]     = useState(() => {
    const stored = localStorage.getItem('token');
    // Si le token est déjà expiré au chargement, on le supprime immédiatement
    if (stored && isTokenExpired(stored)) {
      localStorage.removeItem('token');
      return null;
    }
    return stored;
  });
  const [authNotif, setAuthNotif] = useState(null);

  const showAuthNotif = (message, type = 'success') => {
    setAuthNotif({ message, type });
    setTimeout(() => setAuthNotif(null), 4000);
  };

  const logout = useCallback((expired = false) => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    if (expired) {
      showAuthNotif('Votre session a expiré. Veuillez vous reconnecter.', 'error');
    } else {
      showAuthNotif('Vous avez été déconnecté avec succès.', 'success');
    }
  }, []);

  const loadUser = useCallback(async () => {
    try {
      const res = await axios.get('/auth/me');
      setUser(res.data.user);
    } catch (error) {
      const status = error.response?.status;
      // 401 = token expiré ou invalide côté serveur
      logout(status === 401);
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      // Double vérification : token expiré pendant la session (onglet resté ouvert)
      if (isTokenExpired(token)) {
        logout(true);
        setLoading(false);
        return;
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      loadUser();

      // Timer qui déconnecte automatiquement à l'expiration exacte du token
      const payload = JSON.parse(atob(token.split('.')[1]));
      const msUntilExpiry = payload.exp * 1000 - Date.now();
      const timer = setTimeout(() => logout(true), msUntilExpiry);
      return () => clearTimeout(timer);
    } else {
      setLoading(false);
    }
  }, [token, loadUser, logout]);

  const login = async (email, password) => {
    try {
      const res = await axios.post('/auth/login', { email, password });
      const newToken = res.data.token;
      setToken(newToken);
      setUser(res.data.user);
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return { success: true, message: res.data.message };
    } catch (error) {
      const status = error.response?.status;
      const msg    = error.response?.data?.message;
      let errorMessage = 'Erreur de connexion. Veuillez réessayer.';
      if (status === 401)      errorMessage = msg || 'Email ou mot de passe incorrect.';
      else if (status === 403) errorMessage = 'Votre compte a été désactivé. Contactez le support.';
      else if (status === 400) errorMessage = msg || 'Veuillez remplir tous les champs.';
      else if (status === 429) errorMessage = 'Trop de tentatives. Veuillez patienter.';
      else if (!error.response) errorMessage = 'Impossible de contacter le serveur. Vérifiez votre connexion.';
      throw new Error(errorMessage);
    }
  };

  const register = async (name, email, password) => {
    try {
      const res = await axios.post('/auth/register', { name, email, password });
      const newToken = res.data.token;
      setToken(newToken);
      setUser(res.data.user);
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return { success: true, message: res.data.message };
    } catch (error) {
      const status = error.response?.status;
      const msg    = error.response?.data?.message;
      let errorMessage = 'Erreur lors de la création du compte.';
      if (status === 400 && msg?.includes('email'))        errorMessage = 'Cet email est déjà utilisé.';
      else if (status === 400 && msg?.includes('6 caract')) errorMessage = 'Le mot de passe doit contenir au moins 6 caractères.';
      else if (status === 400)  errorMessage = msg || 'Veuillez remplir tous les champs correctement.';
      else if (!error.response) errorMessage = 'Impossible de contacter le serveur. Vérifiez votre connexion.';
      throw new Error(errorMessage);
    }
  };

  const value = { user, token, loading, authNotif, login, register, logout };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};